import Spin from '@/components/basic/spin';
import theme from '@/style';
import React from 'react';
import {ScrollView, View, StyleSheet} from 'react-native';
import {getIGameUserRanking} from '../result-service';
import {IGameTopItem} from '../result.type';
import LinearGradient from '@/components/basic/linear-gradient/linear-gradient';
import Text from '@/components/basic/text';
import ListItem from './list-item';
import ScratchDraw from './draw';
import NoData from '@/components/basic/error-pages/no-data';
import {currentBrand} from '@/brands';
import {useTranslation} from 'react-i18next';
import ShowMoreButton from '../components/show-more-button';

const defaultVisibleCount = 10;

const ScratchResult = ({isActive = false}: {isActive?: boolean}) => {
  const [loading, setLoading] = React.useState(true);
  const [list, setList] = React.useState<IGameTopItem[]>([]);
  const [hasDraw, setHasDraw] = React.useState(false);
  const [showMore, setShowMore] = React.useState(false);
  const isAnna = currentBrand.key === 'anna';
  const {t} = useTranslation();

  const getList = async () => {
    try {
      setLoading(true);
      setShowMore(false);
      const res = await getIGameUserRanking(1);
      setList(res);
    } finally {
      setLoading(false);
    }
  };

  const visibleList = React.useMemo(() => {
    return showMore ? list : list.slice(0, defaultVisibleCount);
  }, [list, showMore]);

  React.useEffect(() => {
    if (isActive) {
      getList();
    }
  }, [isActive]);

  return (
    <Spin
      loading={loading}
      style={[theme.flex.flex1, theme.background.lightGrey]}>
      <ScrollView
        contentContainerStyle={[
          isAnna
            ? styles.annaContent
            : {paddingBottom: theme.paddingSize.l * 2},
          list.length === 0 && !hasDraw && styles.emptyContent,
        ]}>
        {!loading && <ScratchDraw onVisibleChange={setHasDraw} />}
        {!loading && list.length > 0 && (
          <View
            style={[
              hasDraw ? theme.margin.topl : null,
              theme.overflow.hidden,
              styles.rankHeaderWrap,
            ]}>
            <LinearGradient
              colors={['#FFFFFF', '#FFFFFF00']}
              style={[theme.padding.l, theme.flex.row, theme.flex.centerByCol]}>
              <Text
                main
                fontFamily="fontInterBold"
                size="medium"
                style={isAnna ? styles.annaLeaderTitle : undefined}>
                Today's leaderboard
              </Text>
            </LinearGradient>
          </View>
        )}
        {!loading && list.length === 0 ? null : (
          <View style={[styles.tableHeader]}>
            <View style={[theme.flex.flex1]}>
              <Text style={styles.tableHeaderText}>SCRATCH OFF</Text>
            </View>
            <View style={styles.userCol}>
              <Text textAlign="center" style={styles.tableHeaderText}>
                USER
              </Text>
            </View>
            <View style={styles.userCol}>
              <Text textAlign="right" style={styles.tableHeaderText}>
                BONUS
              </Text>
            </View>
          </View>
        )}
        {!loading && list.length === 0 && (
          <View style={[theme.flex.center, theme.flex.flex1, styles.emptyWrap]}>
            <NoData />
          </View>
        )}
        {visibleList.map((item, _i) => {
          return <ListItem key={_i} {...item} index={_i} />;
        })}
        {!loading && list.length > defaultVisibleCount && !showMore && (
          <View style={styles.moreWrap}>
            <ShowMoreButton
              label={t('home.label.showMore')}
              onPress={() => setShowMore(true)}
            />
          </View>
        )}
      </ScrollView>
    </Spin>
  );
};

const styles = StyleSheet.create({
  annaContent: {
    paddingTop: 12,
    paddingBottom: 72,
  },
  emptyContent: {
    flexGrow: 1,
  },
  rankHeaderWrap: {
    borderTopLeftRadius: theme.paddingSize.s,
    borderTopRightRadius: theme.paddingSize.s,
  },
  annaLeaderTitle: {
    letterSpacing: 0.2,
  },
  tableHeader: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    backgroundColor: '#E9ECF5',
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    columnGap: 8,
    flexDirection: 'row',
    marginHorizontal: 12,
  },
  tableHeaderText: {
    fontSize: 11,
    fontWeight: '500',
    color: '#7F8896',
    letterSpacing: 0.2,
  },
  userCol: {
    width: 80,
  },
  emptyWrap: {
    minHeight: 400,
  },
  moreWrap: {
    padding: 12,
    marginHorizontal: 12,
    backgroundColor: '#FFFFFF',
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
  },
});

export default ScratchResult;
